import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth, API } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  ArrowRight, Download, Edit, Loader2, FileText, AlertCircle, Home,
} from 'lucide-react';
import SiteFooter from '../components/SiteFooter';
import ModonLogo from '../components/ModonLogo';
import { toast } from 'sonner';

// Clean PDF viewer page. Used by both factory and admin.
// No editor panel. Just shows the saved PDF (if exists) with controls.
export default function PDFViewerPage({ adminMode = false }) {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState(null);
  const [dataUri, setDataUri] = useState(null);
  const [error, setError] = useState('');
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Fetch metadata (title + factory)
        const metaUrl = adminMode ? `${API}/admin/sds/${id}` : `${API}/sds/${id}`;
        const m = await axios.get(metaUrl);
        if (!cancelled) setMeta(m.data);
        // Fetch PDF binary via axios with auth header
        const pdfUrl = `${API}/sds/${id}/pdf`;
        const r = await axios.get(pdfUrl, { responseType: 'blob' });
        if (cancelled) return;
        const blob = r.data;
        setBlobUrl(URL.createObjectURL(blob));
        const reader = new FileReader();
        reader.onload = () => { if (!cancelled) setDataUri(reader.result); };
        reader.readAsDataURL(blob);
      } catch (e) {
        if (!cancelled) {
          const status = e?.response?.status;
          if (status === 404) {
            // No saved PDF for this SDS — auto-redirect to editor with preview=1
            // The editor will generate and display the PDF from current data + upload it for next time.
            const editPath = adminMode ? `/admin/sds/edit/${id}?preview=1` : `/factory/edit/${id}?preview=1`;
            nav(editPath, { replace: true });
            return;
          } else {
            setError('LOAD_ERROR');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, adminMode, nav]);

  // Revoke blob on unmount
  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }, [blobUrl]);

  const safeFactoryName = () => {
    const raw = (meta?.factoryName || meta?.title || 'SDS-Report').toString().trim();
    return raw.replace(/[\\\/:*?"<>|]+/g, '').replace(/\s+/g, '_') || 'SDS-Report';
  };

  const handleDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `${safeFactoryName()}_SDS.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const backHref = adminMode ? '/admin' : '/factory';
  const editHref = adminMode ? `/admin/sds/edit/${id}` : `/factory/edit/${id}`;

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col" dir="rtl">
      {/* Top toolbar (no editor!) */}
      <header className="bg-white border-b shadow px-4 py-2 flex items-center justify-between flex-wrap gap-2" dir={isEn ? 'ltr' : 'rtl'}>
        <div className="flex items-center gap-2 min-w-0">
          {/* Navigation cluster: Back + Home */}
          <div className="flex items-center bg-[#1a4d80] rounded-md overflow-hidden shadow-sm" data-testid="pdf-view-nav-cluster">
            <Button
              size="sm"
              onClick={() => nav(-1)}
              className="bg-transparent hover:bg-white/15 text-white rounded-none h-9 px-3"
              data-testid="pdf-view-back-btn"
              title={t('common.back')}
            >
              <ArrowRight size={16} className={isEn ? 'rotate-180 mr-1' : 'ml-1'} />
              <span className="hidden sm:inline">{t('common.back')}</span>
            </Button>
            <div className="w-px h-5 bg-white/30"/>
            <Link to={backHref}>
              <Button
                size="sm"
                className="bg-transparent hover:bg-white/15 text-white rounded-none h-9 px-3"
                data-testid="pdf-view-home-btn"
                title={t('common.home')}
              >
                <Home size={16} className="ml-1" />
                <span className="hidden sm:inline">{t('common.home')}</span>
              </Button>
            </Link>
          </div>
          <ModonLogo size={36}/>
          <div className="text-sm font-bold text-slate-800 truncate max-w-md">
            <FileText size={14} className="inline ml-1 text-[#1a4d80]"/>
            {meta?.title || t('factory.untitled')}
            {meta?.factoryName && <span className="text-slate-500 font-normal"> — {meta.factoryName}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to={editHref}>
            <Button size="sm" variant="outline" className="text-xs" data-testid="pdf-view-edit-btn">
              <Edit size={14} className="ml-1" /> تعديل التقرير
            </Button>
          </Link>
          <Button size="sm" variant="outline" onClick={handleDownload} disabled={!blobUrl} className="text-xs" data-testid="pdf-view-download-btn">
            <Download size={14} className="ml-1" /> تحميل
          </Button>
        </div>
      </header>

      <main className="flex-1 relative bg-slate-300">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center text-slate-700">
            <Loader2 className="animate-spin ml-2" size={20}/> جارٍ تحميل ملف PDF...
          </div>
        ) : error === 'NOT_FOUND' ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-white rounded-xl p-8 shadow-lg max-w-md text-center" dir="rtl">
              <AlertCircle className="mx-auto text-amber-500 mb-3" size={48}/>
              <h2 className="text-lg font-bold text-slate-900 mb-2">ملف PDF غير متوفر بعد</h2>
              <p className="text-sm text-slate-600 mb-4">
                لم يتم إنشاء ملف PDF لهذا التقرير بعد. افتح المحرر، عدّل أو احفظ التقرير لإنشاء ملف PDF تلقائياً.
              </p>
              <Link to={editHref}>
                <Button className="bg-[#1a4d80] hover:bg-[#143d68] text-white" data-testid="pdf-view-generate-btn">
                  <Edit size={14} className="ml-1"/> فتح المحرر لإنشاء PDF
                </Button>
              </Link>
            </div>
          </div>
        ) : error ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-white rounded-xl p-8 shadow-lg max-w-md text-center">
              <AlertCircle className="mx-auto text-red-500 mb-3" size={48}/>
              <h2 className="text-lg font-bold text-slate-900 mb-2">تعذر تحميل الملف</h2>
              <Button onClick={() => window.location.reload()} variant="outline">إعادة المحاولة</Button>
            </div>
          </div>
        ) : dataUri ? (
          <object
            data={dataUri}
            type="application/pdf"
            className="absolute inset-0 w-full h-full"
            data-testid="sds-pdf-frame"
          >
            <embed src={dataUri} type="application/pdf" className="w-full h-full" />
            <div className="absolute inset-0 flex items-center justify-center bg-white">
              <div className="max-w-md text-center p-6">
                <FileText className="mx-auto text-[#1a4d80] mb-3" size={48}/>
                <h3 className="font-bold text-lg mb-2">تعذّر عرض PDF داخل المتصفح</h3>
                <p className="text-sm text-slate-600 mb-4">
                  يبدو أن متصفحك لا يدعم عرض ملفات PDF المضمّنة. اضغط أدناه للتحميل.
                </p>
                <Button onClick={handleDownload} className="bg-[#1a4d80] hover:bg-[#143d68] text-white">
                  <Download size={14} className="ml-1"/> تحميل ملف PDF
                </Button>
              </div>
            </div>
          </object>
        ) : null}
      </main>
    </div>
  );
}
